'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { AlertTriangle, ArrowLeft, BadgePercent, Check, ChevronRight, CircleCheck, FileUp, Gift, HandCoins, Info, LoaderCircle, MapPinPlus, Package, PackageX, Plus, ShoppingBag, Store, Tag, Ticket, Truck, Wallet, X } from 'lucide-react'
import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import Select from '@/components/elements/Select'
import Textarea from '@/components/elements/Textarea'
import Checkbox from '@/components/elements/Checkbox'
import ImagePicker from '@/components/elements/ImagePicker'
import { DateTimePicker } from '@/components/elements/DateTimePicker'
import Modal from '@/components/elements/Modal'
import { Skeleton } from '@/components/ui/skeleton'
import ProcessingState from '@/components/elements/ProcessingState'
import Tabs from '@/components/elements/Tabs'
import { formatImageURL } from '@/utils/util'
import { useCart, useAddress, usePayment, useDelivery, useCustomerDetails, useOrderTotal, useCheckoutSubmit, useCustomFields } from '@/lib/commerce/hooks'
import { formatPrice, formatDateTimeNormal, getAddressTypeBadge, getBuyXTriggerCount, getBuyXGetYRewardRule, isCouponActive, isGiftCardActive, isLoyaltyActive } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { toast } from '@/lib/toast'
import TermsDrawer from '@/components/blocks/TermsDrawer'
import Qpay from '@/assets/qpay.svg'
import TransferIcon from '@/assets/transfer.png'
import Storepay from '@/assets/storepay.png'
import Pocket from '@/assets/pocket.png'
import Lend from '@/assets/lendmn.png'
import Omniway from '@/assets/omniway.png'
import Booklo from '@/assets/booklo.jpg'
import CheckoutCard from './CheckoutCard'
import { getLoyaltyVouchersApi, getLoyaltyTiersApi } from '@/lib/commerce/services/loyalty-api'

type TermsType = 'delivery' | 'payment' | 'service'

function PaymentRow({ active, onClick, disabled, title, sub, icon, children }: { active: boolean, onClick: () => void, disabled?: boolean, title: string, sub?: React.ReactNode, icon: React.ReactNode, children?: React.ReactNode }) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`rounded-2xl border transition-all flex flex-col p-4 ${disabled ? 'opacity-50 cursor-not-allowed border-foreground/10 bg-white' : `cursor-pointer ${active ? 'border-foreground bg-foreground/3' : 'border-foreground/10 bg-white hover:border-foreground/30'}`}`}
    >
      <div className="flex items-center gap-3.5">
        <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 ${active ? 'border-[6px] border-foreground bg-white' : 'border-1.5 border-foreground/25'}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-foreground">{title}</div>
          {sub && <div className="text-[12px] text-foreground/55 mt-0.5">{sub}</div>}
        </div>
        {icon}
      </div>
      {children}
    </div>
  )
}

export default function Checkout({ vendor, address, branches, customerAddress, subscription }: { vendor: any, address: any, branches: any, customerAddress: any, subscription: any }) {
  const pathname = usePathname() || ''
  const router = useRouter()
  const { data: session } = useSession()
  const { cart, loading, initialLoading, isValidating, validateCart, canCheckout, allProducts, appliedCoupon, applyCoupon, removeCoupon, couponDiscount, appliedGiftCard, applyGiftCard, removeGiftCard, giftCardDiscount, activeOrderDiscount, hasFreeShipping, freeShippingProgress, orderDiscountAmount, amount, sale, salePriceSavings, discountSavings, discountBreakdown, buyXGetYInfo, addRewardItem, availableOrderRewardItems, availableCouponRewardItems, rewardLoading, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, loyaltySaleOffsetAmount, appliedLoyaltyVoucher, applyLoyaltyVoucher, removeLoyaltyVoucher } = useCart()
  const displaySaleSavings = Math.max(0, salePriceSavings - loyaltySaleOffsetAmount)

  // Both need their module active, not just the vendor's customer-facing switch —
  // otherwise a disabled module leaves an input here that the backend refuses.
  const canUseCoupon = isCouponActive(vendor)
  const canUseGiftCard = isGiftCardActive(vendor)
  const loyaltyEnabled = isLoyaltyActive(vendor) && !!session?.user?.id
  const [loyaltyVouchers, setLoyaltyVouchers] = useState<any[]>([])
  const [loyaltyProgram, setLoyaltyProgram] = useState<any>(null)
  const canUseLoyaltyVoucher = loyaltyEnabled && (loyaltyVouchers.length > 0 || !!appliedLoyaltyVoucher)
  const appliedVoucherAmount = Number(appliedLoyaltyVoucher?.remainingAmount ?? appliedLoyaltyVoucher?.amount) || 0

  useEffect(() => {
    if (!loyaltyEnabled) return
    let cancelled = false
    getLoyaltyVouchersApi({ vendorId: vendor?.id || vendor, session: session || undefined }).then(({ data }) => {
      if (!cancelled && Array.isArray(data)) setLoyaltyVouchers(data.filter((voucher: any) => voucher.status === 'active'))
    }).catch(() => {})
    getLoyaltyTiersApi({ vendorId: vendor?.id || vendor }).then(({ data }) => {
      if (!cancelled && data) setLoyaltyProgram(data)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [loyaltyEnabled])

  const addressApi = useAddress({ address, vendor, customerAddress, isLoggedIn: !!session?.user?.id })
  const {
    addressType, setAddressType,
    selectedAddress, setSelectedAddress, setAddressObj,
    setCity,
    setDuureg,
    setKhoroo,
    bair, setBair,
    toot, setToot,
    additionalAddress, setAdditionalAddress,
    houseNum, setHouseNum,
    street, setStreet,
    building, setBuilding,
    floor, setFloor,
    officeName, setOfficeName,
    selectedProvince, setSelectedProvince,
    selectedDistrict, setSelectedDistrict,
    selectedSubdistrict, setSelectedSubdistrict,
    provinces, districts, subdistricts,
    isAddressDeliverable,
    saveNewAddress,
  } = addressApi
  const [openAddNewAddress, setOpenAddNewAddress] = useState(false)
  const [newAddressLoading, setNewAddressLoading] = useState(false)
  const addNewAddress = async () => {
    setNewAddressLoading(true)
    const result = await saveNewAddress()
    setNewAddressLoading(false)
    if (!result?.ok) return
    setOpenAddNewAddress(false)
    router.refresh()
  }

  const products = cart?.products || []
  const deliveryApi = useDelivery({ vendor, branches })
  const { selectedDelivery, selectedBranch, setSelectedBranch, selectDelivery, hasDigitalProduct, hasDeliveryOption, hasPickupOption, showDeliverySection } = deliveryApi
  const { subPrice, loyaltyDiscount, validLoyaltyVoucherDiscount, deliveryPrice, totalPrice, isFreeOrder, depositAmount, balanceAmount, hasDeposit, payNowAmount } = useOrderTotal({ vendor, selectedDelivery })
  const voucherExceedsCart = appliedVoucherAmount > subPrice
  const paymentApi = usePayment({ vendor, products, isFreeOrder, hasDeposit })
  // 'Барааг авахдаа төлөх' means nothing is due now, so it supersedes the deposit.
  const effectiveDeposit = paymentApi.selectedPayment === 'onDelivery' ? 0 : depositAmount
  const { selectedPayment, storepayPhone, setStorepayPhone, lendPhone, setLendPhone, isMethodAllowedForCart, canPayOnDelivery, selectPayment } = paymentApi

  const customerDetailsApi = useCustomerDetails()
  const { fullName, setFullName, email, setEmail, phone, setPhone, additionalPhone, setAdditionalPhone } = customerDetailsApi
  const customFieldsApi = useCustomFields()
  const { productValues: customFieldValues, orderValues: orderCustomFieldValues, setProductField, setOrderField, uploadProductFile, uploadOrderFile, isUploadingProductField, isUploadingOrderField, buildProductKey } = customFieldsApi
  const productsWithFields = products.filter((p: any) => p.fields?.length > 0)
  const [termsOpen, setTermsOpen] = useState(false)
  const [termsType, setTermsType] = useState<TermsType>('delivery')
  const [accepted, setAccepted] = useState(true)
  const [couponCode, setCouponCode] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [giftCode, setGiftCode] = useState('')
  const [giftLoading, setGiftLoading] = useState(false)
  const [giftError, setGiftError] = useState('')

  const onApplyCoupon = async () => {
    if (!couponCode.trim() || couponLoading) return
    setCouponLoading(true)
    setCouponError('')
    try {
      const res = await applyCoupon(couponCode.trim())
      if (!res?.success) setCouponError(res?.error || 'Код буруу байна')
      else setCouponCode('')
    } finally { setCouponLoading(false) }
  }

  const onApplyGift = async () => {
    if (!giftCode.trim() || giftLoading) return
    setGiftLoading(true)
    setGiftError('')
    try {
      const ok = await applyGiftCard(giftCode.trim())
      if (!ok) setGiftError('Бэлгийн карт буруу байна')
      else setGiftCode('')
    } finally { setGiftLoading(false) }
  }

  const { submit: submitCheckout, isPlacingOrder } = useCheckoutSubmit({
    vendor, subscription,
    customerDetails: customerDetailsApi,
    customFields: customFieldsApi,
    address: addressApi,
    payment: paymentApi,
    delivery: deliveryApi,
    totalPrice, payNowAmount, isFreeOrder, hasDigitalProduct, hasDeliveryOption, showDeliverySection,
  })

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  const openTerms = (t: TermsType) => { setTermsType(t); setTermsOpen(true) }

  const hasProductFields = subscription?.canUseCustomFields && productsWithFields.length > 0
  const hasOrderFields = subscription?.canUseCustomFields && vendor?.orderFields?.length > 0
  let nextSectionN = 1
  const productFieldsSection = hasProductFields ? ++nextSectionN : 0
  const orderFieldsSection = hasOrderFields ? ++nextSectionN : 0
  const deliverySection = showDeliverySection ? ++nextSectionN : 0
  const paymentSection = !isFreeOrder ? ++nextSectionN : 0
  const belowMinAmount = vendor?.orderMinAmount > 0 && subPrice < vendor.orderMinAmount

  return (
    <>
      <TermsDrawer isOpen={termsOpen} onClose={() => setTermsOpen(false)} type={termsType} terms={vendor?.terms || {}} vendor={vendor} />

      <section className="bg-cream pt-6 pb-16 min-h-screen">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
          <div className="mb-6">
            <button onClick={() => router.push(navigateUrl('/cart', pathname))} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground/60 hover:text-foreground transition-colors cursor-pointer">
              <ArrowLeft className="size-3.5" /> Сагс руу буцах
            </button>
          </div>

          {initialLoading ? (
            <CheckoutSkeleton />
          ) : products.length === 0 && isPlacingOrder ? (
            <ProcessingState className="py-20" titleClassName="text-foreground text-[28px] font-bold tracking-[-0.02em]" descriptionClassName="text-[15px] text-foreground/55" />
          ) : products.length === 0 ? (
            <div className="py-20 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-3xl bg-[#F1EEE7] inline-flex items-center justify-center mb-5">
                <ShoppingBag className="size-10 text-foreground/40" />
              </div>
              <h1 className="text-[28px] font-bold font-gilroy tracking-[-0.02em] text-foreground">Таны сагс хоосон байна</h1>
              <p className="mt-2 text-[15px] text-foreground/55 max-w-md">Та яг одоо сагсандаа бараа нэмээрэй!</p>
              <a href={navigateUrl('/product', pathname)} className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors">
                <ShoppingBag className="size-4" /> Дэлгүүр хэсэх
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-7 lg:gap-10 items-start">
              <div className="space-y-4">
                <h1 className="font-bold font-gilroy tracking-[-0.03em] leading-[0.98] text-foreground mb-2" style={{ fontSize: 'clamp(2.25rem, 4vw, 3rem)' }}>
                  Хүргэлт &amp; төлбөр.
                </h1>

                {/* 1. Customer details */}
                <SectionCard n={1} title="Холбоо барих">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Нэр" placeholder="Таны нэр" value={fullName} onChange={(e: any) => setFullName(e.target.value)} container="col-span-full" autoFocus />
                    <Input label="Имэйл хаяг" type="email" placeholder="example@shop.mn" value={email} onChange={(e: any) => setEmail(e.target.value)} disabled={!!session?.user?.email} container="col-span-full" required={hasDigitalProduct} description={hasDigitalProduct ? 'Дижитал бараа авч байгаа тул имэйл хаяг заавал оруулна уу.' : undefined} />
                    <Input label="Утасны дугаар" type="tel" inputMode="numeric" placeholder="00000000" value={phone} onChange={(e: any) => setPhone(e.target.value)} required />
                    <Input label="Нэмэлт утас" type="tel" inputMode="numeric" placeholder="00000000" value={additionalPhone} onChange={(e: any) => setAdditionalPhone(e.target.value)} />
                  </div>
                </SectionCard>

                {hasProductFields && (
                  <SectionCard n={productFieldsSection} title="Бүтээгдэхүүний нэмэлт мэдээлэл">
                    <div className="space-y-3">
                      {productsWithFields.map((product: any) => {
                        return (
                          <div key={product.id} className="rounded-2xl border border-foreground/10 overflow-hidden">
                            <div className="w-full flex items-center gap-3 px-4 py-3">
                              <div className="w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-cream inline-flex items-center justify-center">
                                {product.variant?.images?.[0] ? (
                                  <img src={formatImageURL(product.variant.images[0])} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Package className="size-5 text-foreground/40" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{product.name}</div>
                                <div className="text-[12px] text-foreground/55 mt-0.5">Шаардагдах нэмэлт {product.fields.length} мэдээлэл</div>
                              </div>
                            </div>
                            {(
                              <div className="px-4 pb-4 pt-1 space-y-4 border-t border-foreground/10">
                                {product.fields.map((field: any, i: number) => {
                                  const fieldKey = buildProductKey(product.id, field.name)
                                  const value = customFieldValues[fieldKey] ?? field.value ?? ''
                                  return (
                                    <CustomField
                                      key={i}
                                      field={field}
                                      value={value}
                                      onChange={(v) => setProductField(fieldKey, v)}
                                      onFile={(f) => uploadProductFile(fieldKey, f)}
                                      uploading={isUploadingProductField(fieldKey)}
                                      idPrefix={`pfield-${product.id}`}
                                    />
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </SectionCard>
                )}

                {hasOrderFields && (
                  <SectionCard n={orderFieldsSection} title="Захиалгын нэмэлт мэдээлэл">
                    <div className="space-y-4">
                      {vendor.orderFields.map((field: any, i: number) => {
                        const value = orderCustomFieldValues[field.name] ?? field.value ?? ''
                        return (
                          <CustomField
                            key={i}
                            field={field}
                            value={value}
                            onChange={(v) => setOrderField(field.name, v)}
                            onFile={(f) => uploadOrderFile(field.name, f)}
                            uploading={isUploadingOrderField(field.name)}
                            idPrefix="ofield"
                          />
                        )
                      })}
                    </div>
                  </SectionCard>
                )}

                {/* 2. Delivery */}
                {showDeliverySection && (
                  <SectionCard n={deliverySection} title="Хүргэлтийн арга">
                    {!hasDeliveryOption && hasPickupOption && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-[12.5px] text-amber-800">
                        Манай дэлгүүр одоогоор хүргэлтийн үйлчилгээ үзүүлэхгүй тул та зөвхөн салбараас ирж авах боломжтой.
                      </div>
                    )}
                    {selectedDelivery === 'delivery' && hasDeliveryOption && vendor?.isBooklo ? (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-[12.5px] text-amber-800">
                        <strong className="font-semibold">Анхаар:</strong> Зөвхөн Booklo хүргэлтийн бүсэд багтах дүүрэг, хороо сонгогдоно.
                      </div>
                    ) : selectedDelivery === 'delivery' && hasDeliveryOption && !vendor?.canDeliverCountryside && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-[12.5px] text-amber-800">
                        <strong className="font-semibold">Анхаар:</strong> Манай дэлгүүр зөвхөн Улаанбаатар хот дотор хүргэлт хийнэ.
                      </div>
                    )}
                    <div className={`grid grid-cols-1 gap-3 ${hasDeliveryOption && hasPickupOption ? 'sm:grid-cols-2' : ''}`}>
                      {hasDeliveryOption && (
                        <button type="button" onClick={() => selectDelivery('delivery')} className={`rounded-2xl border p-4 text-left transition-colors cursor-pointer ${selectedDelivery === 'delivery' ? 'border-foreground bg-foreground/3' : 'border-foreground/10 bg-white hover:border-foreground/30'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 font-semibold text-[14px] text-foreground">
                              {vendor?.isBooklo ? <Image src={Booklo} alt="Booklo" width={16} height={16} className="size-4 rounded object-contain" /> : <Truck className="size-4" />} Хүргэлтээр авах
                            </div>
                            {selectedDelivery === 'delivery' && <CircleCheck className="size-4.5 text-foreground" />}
                          </div>
                          <div className="text-[12px] text-foreground/60 mt-1.5">
                            {vendor?.isBooklo ? <span className="text-orange-600">Booklo хүргэлт 6&apos;500₮</span> : <>
                              {vendor?.deliveryType === 'freeDelivery' && <span className="text-emerald-600">Үнэгүй хүргэлт</span>}
                              {vendor?.deliveryType === 'constantDelivery' && vendor?.deliveryPrice > 0 && <span className="text-orange-600">{formatPrice(vendor.deliveryPrice)} хүргэлтийн төлбөр</span>}
                              {vendor?.deliveryType === 'minOrderAmount' && vendor?.minOrderAmount > 0 && <span className="text-blue-600">{formatPrice(vendor.minOrderAmount)}-аас дээш үнэгүй</span>}
                              {vendor?.deliveryType === 'laterDelivery' && <span className="text-amber-600">Үнэ тусдаа тооцогдоно</span>}
                            </>}
                          </div>
                        </button>
                      )}
                      {hasPickupOption && (
                        <button type="button" onClick={() => selectDelivery('pickup')} className={`rounded-2xl border p-4 text-left transition-colors cursor-pointer ${selectedDelivery === 'pickup' ? 'border-foreground bg-foreground/3' : 'border-foreground/10 bg-white hover:border-foreground/30'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 font-semibold text-[14px] text-foreground">
                              <Store className="size-4" /> Очиж авах
                            </div>
                            {selectedDelivery === 'pickup' && <CircleCheck className="size-4.5 text-foreground" />}
                          </div>
                          <div className="text-[12px] text-foreground/60 mt-1.5">Салбараас ирж авна</div>
                        </button>
                      )}
                    </div>

                    {selectedDelivery === 'delivery' && hasDeliveryOption && (
                      <div className="mt-5">
                        <div className="text-[13px] font-semibold text-foreground mb-3">Хүргэлтийн хаяг</div>
                        {session?.user?.id ? (
                          <div className="space-y-2.5">
                            {customerAddress?.length > 0 ? customerAddress.map((addr: any) => {
                              const isDisabled = (!vendor?.canDeliverCountryside && addr.city !== 'Улаанбаатар') || !isAddressDeliverable(addr)
                              const isActive = selectedAddress === addr.id
                              return (
                                <button
                                  key={addr.id}
                                  type="button"
                                  disabled={isDisabled}
                                  onClick={() => {
                                    if (isDisabled) return
                                    setSelectedAddress(addr.id)
                                    setAddressObj({
                                      city: addr.city, duureg: addr.duureg, khoroo: addr.khoroo,
                                      bair: addr.bair, toot: addr.toot, additionalAddress: addr.additionalAddress,
                                      type: addr.type, houseNum: addr.houseNum, street: addr.street,
                                      building: addr.building, floor: addr.floor, officeName: addr.officeName,
                                    })
                                  }}
                                  className={`w-full text-left rounded-2xl border p-4 transition-colors ${isDisabled ? 'opacity-50 cursor-not-allowed border-foreground/10 bg-foreground/2' : `cursor-pointer ${isActive ? 'border-foreground bg-foreground/3' : 'border-foreground/10 bg-white hover:border-foreground/30'}`}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="inline-flex items-center mb-2">{getAddressTypeBadge(addr.type)}</div>
                                      <div className="text-[14px] font-semibold text-foreground">{addr.city}, {addr.duureg}, {addr.khoroo}</div>
                                      <div className="text-[12.5px] text-foreground/55 mt-0.5">
                                        {addr.type === 'apartment' && `${addr.bair}-р байр, тоот ${addr.toot}`}
                                        {addr.type === 'house' && `${addr.street ? `${addr.street}, ` : ''}${addr.houseNum}`}
                                        {addr.type === 'office' && `${addr.building}, ${addr.floor}-р давхар, тоот ${addr.toot}${addr.officeName ? ` (${addr.officeName})` : ''}`}
                                        {!addr.type && `${addr.bair}-р байр, тоот ${addr.toot}`}
                                      </div>
                                      {addr.additionalAddress && <div className="text-[12.5px] text-foreground/55 mt-0.5">{addr.additionalAddress}</div>}
                                      {isDisabled && (
                                        <span className="mt-2 inline-flex text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Хүргэлт боломжгүй</span>
                                      )}
                                    </div>
                                    {isActive && !isDisabled && <CircleCheck className="size-5 text-foreground shrink-0" />}
                                  </div>
                                </button>
                              )
                            }) : (
                              <div className="rounded-2xl border border-dashed border-foreground/20 p-5 text-center text-[13px] text-foreground/55">
                                Хадгалсан хаяг алга. Профайл цэснээс шинэ хаяг нэмнэ үү.
                              </div>
                            )}
                            {customerAddress?.length > 0 && customerAddress.every((addr: any) => !isAddressDeliverable(addr)) && (
                              <div className="p-3 bg-red-50 border border-red-200 rounded-2xl">
                                <div className="text-[12.5px] text-red-700">Таны хадгалсан хаягууд руу хүргэлт хийх боломжгүй байна. Шинэ хаяг нэмнэ үү.</div>
                              </div>
                            )}
                            <Button width="full" icon={MapPinPlus} variant="outline" className="rounded-full!" onClick={() => setOpenAddNewAddress(true)}>
                              Шинэ хаяг нэмэх
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <Tabs
                              tabs={[
                                { label: 'Орон сууц', value: 'apartment', content: null },
                                { label: 'Хашаа байшин', value: 'house', content: null },
                                { label: 'Оффис', value: 'office', content: null },
                              ]}
                              defaultValue={addressType}
                              onValueChange={(v: string) => setAddressType(v as 'apartment' | 'house' | 'office')}
                              className="w-full"
                            />
                            <Select label="Хот / Аймаг" items={!vendor?.canDeliverCountryside ? provinces.filter((p: any) => p.value === 'Улаанбаатар') : provinces} value={selectedProvince || ''} onChange={(v: string) => { setSelectedProvince(v); setCity(v); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }} required />
                            <Select label="Дүүрэг / Сум" value={selectedDistrict || ''} items={districts} required onChange={(v: string) => { setSelectedDistrict(v); setDuureg(v); setSelectedSubdistrict(null); setKhoroo('') }} />
                            <Select label="Хороо / Баг" value={selectedSubdistrict || ''} items={subdistricts} required onChange={(v: string) => { setSelectedSubdistrict(v); setKhoroo(v) }} />
                            {addressType === 'apartment' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Input label="Байр" placeholder="Байрны дугаар" value={bair} onChange={(e: any) => setBair(e.target.value)} required />
                                <Input label="Тоот" placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} required />
                              </div>
                            )}
                            {addressType === 'house' && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Input label="Хашааны дугаар" placeholder="Хашааны дугаар" value={houseNum} onChange={(e: any) => setHouseNum(e.target.value)} required />
                                <Input label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={(e: any) => setStreet(e.target.value)} />
                              </div>
                            )}
                            {addressType === 'office' && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={building} onChange={(e: any) => setBuilding(e.target.value)} required />
                                  <Input label="Давхар" placeholder="Давхар" value={floor} onChange={(e: any) => setFloor(e.target.value)} required />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <Input label="Тоот" placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} required />
                                  <Input label="Оффисын нэр" placeholder="Оффисын нэр" value={officeName} onChange={(e: any) => setOfficeName(e.target.value)} />
                                </div>
                              </>
                            )}
                            <Input label="Нэмэлт мэдээлэл" placeholder="Орц, давхар, орцны код гэх мэт" value={additionalAddress} onChange={(e: any) => setAdditionalAddress(e.target.value)} />
                          </div>
                        )}
                      </div>
                    )}

                    {selectedDelivery === 'pickup' && !vendor?.canPickup && (
                      <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-[12.5px] text-amber-800">
                        <strong className="font-semibold">Анхаар:</strong> Салбараас авах үйлчилгээ одоогоор хаагдсан байна.
                      </div>
                    )}

                    {selectedDelivery === 'pickup' && vendor?.canPickup && branches?.length > 0 && (
                      <div className="mt-5">
                        <div className="text-[13px] font-semibold text-foreground mb-3">Салбарын хаяг</div>
                        <div className="space-y-2.5">
                          {branches.map((b: any) => {
                            const isActive = selectedBranch === b.id
                            return (
                              <button
                                key={b.id}
                                type="button"
                                onClick={() => setSelectedBranch(b.id)}
                                className={`w-full text-left rounded-2xl border p-4 transition-colors cursor-pointer ${isActive ? 'border-foreground bg-foreground/3' : 'border-foreground/10 bg-white hover:border-foreground/30'}`}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="text-[14px] font-semibold text-foreground inline-flex items-center gap-2">
                                      <Store className="size-3.5 text-foreground/60" /> {b.name}
                                      {b.isDefault && <span className="text-[10px] font-semibold uppercase tracking-[0.08em] bg-foreground text-cream px-1.5 py-0.5 rounded-full">Үндсэн</span>}
                                    </div>
                                    <div className="text-[12.5px] text-foreground/55 mt-1">{b.address}</div>
                                    {b.phone && <div className="text-[12.5px] text-foreground/55 mt-0.5">Утас: {b.phone}</div>}
                                  </div>
                                  {isActive && <CircleCheck className="size-5 text-foreground shrink-0" />}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </SectionCard>
                )}

                {/* Payment */}
                {!isFreeOrder && (
                  <SectionCard n={paymentSection} title="Төлбөрийн арга">
                    {/* Render order mirrors the auto-select precedence in usePayment,
                        so the pre-selected method is always the first row. */}
                    <div className="space-y-2.5">
                      {canPayOnDelivery && isMethodAllowedForCart('onDelivery') && (
                        <PaymentRow active={selectedPayment === 'onDelivery'} onClick={() => selectPayment('onDelivery')} title="Барааг авахдаа төлөх" sub="Барааг хүлээн авахдаа шууд төлнө" icon={<HandCoins className="h-6 w-6 text-muted-foreground" />} />
                      )}
                      {vendor?.qpayMerchant && subscription?.canUseQPay && vendor?.canQpay !== false && isMethodAllowedForCart('qpay') && (
                        <PaymentRow active={selectedPayment === 'qpay'} onClick={() => selectPayment('qpay')} title="QPay" sub="Бүх банкны аппаар QR код уншуулна уу" icon={<Image src={Qpay} alt="" className="h-6 w-6 object-contain" />} />
                      )}
                      {vendor?.canTransfer && vendor?.canSimple !== false && isMethodAllowedForCart('transfer') && (
                        <PaymentRow active={selectedPayment === 'transfer'} onClick={() => selectPayment('transfer')} title="Дансаар шилжүүлэх" sub="Дэлгүүрийн дансанд шилжүүлэг хийж төлөх" icon={<Image src={TransferIcon} alt="" className="h-6 w-6 object-contain" />} />
                      )}
                      {vendor?.storepayCredentials && vendor?.canStorepay !== false && isMethodAllowedForCart('storepay') && (
                        <PaymentRow
                          active={selectedPayment === 'storepay'}
                          onClick={() => totalPrice >= 100000 && selectPayment('storepay')}
                          disabled={totalPrice < 100000}
                          title="StorePay"
                          sub={totalPrice < 100000 ? 'Зөвхөн 100,000₮-с дээш' : '4-6 хуваан төлөх боломжтой'}
                          icon={<Image src={Storepay} alt="" className="h-6 w-6 object-cover rounded" />}
                        >
                          {selectedPayment === 'storepay' && totalPrice >= 100000 && (
                            <div className="mt-3 pl-8">
                              <Input value={storepayPhone} onChange={(e: any) => setStorepayPhone(e.target.value)} placeholder="StorePay бүртгэлтэй утас" type="text" required />
                            </div>
                          )}
                        </PaymentRow>
                      )}
                      {vendor?.pocketCredentials && vendor?.canPocket !== false && isMethodAllowedForCart('pocket') && (
                        <PaymentRow
                          active={selectedPayment === 'pocket'}
                          onClick={() => totalPrice >= 100000 && selectPayment('pocket')}
                          disabled={totalPrice < 100000}
                          title="PocketZero"
                          sub={totalPrice < 100000 ? 'Зөвхөн 100,000₮-с дээш' : '30-90 хоногт 2-6 хувааж төлөх'}
                          icon={<Image src={Pocket} alt="" className="h-6 w-6 object-cover rounded" />}
                        />
                      )}
                      {vendor?.lendCredentials && vendor?.canLend !== false && isMethodAllowedForCart('lend') && (
                        <PaymentRow
                          active={selectedPayment === 'lend'}
                          onClick={() => totalPrice >= 100000 && selectPayment('lend')}
                          disabled={totalPrice < 100000}
                          title="LendMN"
                          sub={totalPrice < 100000 ? 'Зөвхөн 100,000₮-с дээш' : '30-60 хоногт 1-4 хувааж төлөх'}
                          icon={<Image src={Lend} alt="" className="h-6 w-6 object-contain" />}
                        >
                          {selectedPayment === 'lend' && totalPrice >= 100000 && (
                            <div className="mt-3 pl-8">
                              <Input value={lendPhone} onChange={(e: any) => setLendPhone(e.target.value)} placeholder="LendMN бүртгэлтэй утас" type="text" required />
                            </div>
                          )}
                        </PaymentRow>
                      )}
                      {vendor?.omniwayCredentials && vendor?.canOmniway !== false && isMethodAllowedForCart('omniway') && (
                        <PaymentRow
                          active={selectedPayment === 'omniway'}
                          onClick={() => totalPrice >= 100000 && selectPayment('omniway')}
                          disabled={totalPrice < 100000}
                          title="OmniWay"
                          sub={totalPrice < 100000 ? 'Зөвхөн 100,000₮-с дээш' : 'Хуваан төлөх боломжтой'}
                          icon={<Image src={Omniway} alt="" className="h-6 w-6 object-cover rounded" />}
                        />
                      )}
                    </div>
                  </SectionCard>
                )}
              </div>

              {/* Sticky summary */}
              <aside className="lg:sticky lg:top-24 lg:self-start">
                <div className="bg-white rounded-3xl p-6 lg:p-7 flex flex-col gap-5">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-1.5">Захиалга</div>
                    <div className="text-[22px] font-bold font-gilroy tracking-[-0.015em] text-foreground">Хураангуй</div>
                  </div>

                  <div>
                    {allProducts.map((p: any, i: number) => (
                      <CheckoutCard key={`${p.isRewardItem ? 'reward-' : ''}${p.id}-${p.variant?.id}-${i}`} product={p} />
                    ))}
                  </div>

                  {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
                    const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
                    if (totalRemaining <= 0) return null
                    return (
                      <div key={info.discount.id} className="rounded-2xl bg-cream border border-foreground/10 p-4">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full bg-white text-foreground inline-flex items-center justify-center shrink-0">
                            <Gift className="size-4" strokeWidth={2.25} />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground leading-tight">{info.discount?.name || 'Урамшуулал'}</div>
                            <div className="text-[11.5px] text-foreground/55 mt-0.5">
                              {getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-emerald-700 font-semibold">
                          <Check className="size-3.5" /> {info.triggerCount}/{getBuyXTriggerCount(info.discount)}{totalRemaining > 0 ? ` · ${totalRemaining} бэлэг үлдсэн` : ''}
                        </div>
                        <div className="mt-2.5 space-y-2">
                          {info.rewardProducts.map((reward: any) => {
                            const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                            if (isLockedByOther) return null
                            return (
                              <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-white rounded-xl">
                                <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-[#F1EEE7]">
                                  <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                    {reward.valueType === 'free' ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                    ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                    ) : null}
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-1.5">
                                    <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                    {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={!canAdd || rewardLoading}
                                  onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')}
                                  className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                >
                                  {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : canAdd ? <><Plus className="size-3" /> Нэмэх</> : isOutOfStock ? 'Дууссан' : 'Нэмэгдсэн'}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })())}

                  {availableCouponRewardItems?.length > 0 && (
                    <div className="rounded-2xl bg-cream border border-foreground/10 p-4">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full bg-white text-foreground inline-flex items-center justify-center shrink-0">
                          <Gift className="size-4" strokeWidth={2.25} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-semibold text-foreground leading-tight">{appliedCoupon?.discount?.name || appliedCoupon?.name || 'Купоны бэлэг'}</div>
                          <div className="text-[11.5px] text-foreground/55 mt-0.5">Хөнгөлөлтийн кодын бэлэг сонгоно уу</div>
                        </div>
                      </div>
                      <div className="mt-2.5 space-y-2">
                        {availableCouponRewardItems.map((reward: any) => {
                          const isOutOfStock = isRewardOutOfStock(reward)
                          const rewardPrice = getRewardPrice(reward)
                          return (
                            <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-white rounded-xl">
                              <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-[#F1EEE7]">
                                <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                  {reward.valueType === 'free' ? (
                                    <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                  ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                    <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                  ) : reward.valueType === 'amount' && reward.valueAmount ? (
                                    <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{formatPrice(reward.valueAmount)}</span>
                                  ) : null}
                                </div>
                                <div className="mt-0.5 flex items-center gap-1.5">
                                  <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                  {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={isOutOfStock || rewardLoading}
                                onClick={() => addRewardItem(reward, 'order')}
                                className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                              >
                                {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3" /> Нэмэх</>}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {availableOrderRewardItems?.length > 0 && (
                    <div className="rounded-2xl bg-cream border border-foreground/10 p-4">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-full bg-white text-foreground inline-flex items-center justify-center shrink-0">
                          <Gift className="size-4" strokeWidth={2.25} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-semibold text-foreground leading-tight">Захиалгын бэлэг</div>
                          <div className="text-[11.5px] text-foreground/55 mt-0.5">Захиалгын урамшууллын бэлэг сонгоно уу</div>
                        </div>
                      </div>
                      <div className="mt-2.5 space-y-2">
                        {availableOrderRewardItems.map((reward: any) => {
                          const isOutOfStock = isRewardOutOfStock(reward)
                          const rewardPrice = getRewardPrice(reward)
                          return (
                            <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-white rounded-xl">
                              <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-[#F1EEE7]">
                                <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                  {reward.valueType === 'free' ? (
                                    <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                  ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                    <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                  ) : null}
                                </div>
                                <div className="mt-0.5 flex items-center gap-1.5">
                                  <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                  {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={isOutOfStock || rewardLoading}
                                onClick={() => addRewardItem(reward, 'order')}
                                className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                              >
                                {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3" /> Нэмэх</>}
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                    const subPriceVal = (amount || 0) - (sale || 0)
                    const minAmt = activeOrderDiscount.minOrderAmount
                    const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                    if (!target) return null
                    const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                    if (!discountLabel) return null
                    const reached = subPriceVal >= minAmt
                    return (
                      <div className="rounded-2xl bg-purple-50 border border-purple-100 px-4 py-3">
                        <div className="flex items-start gap-2 text-[13px] text-purple-900">
                          <BadgePercent className="size-4 mt-0.5 shrink-0" />
                          <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex-1 h-1.5 rounded-full bg-purple-200 overflow-hidden">
                            <div className="h-full bg-purple-600 transition-all" style={{ width: `${Math.min((subPriceVal / minAmt) * 100, 100)}%` }} />
                          </div>
                          {reached ? (
                            <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-purple-700 shrink-0">
                              <Check className="size-3.5" /> −{formatPrice(orderDiscountAmount)}
                            </span>
                          ) : (
                            <span className="text-[12px] font-semibold text-purple-700 tabular-nums shrink-0">{formatPrice(minAmt - subPriceVal)} дутуу</span>
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {(canUseCoupon || canUseGiftCard || canUseLoyaltyVoucher) && (
                    <Tabs
                      tabs={[
                        ...(canUseCoupon ? [{
                          label: <><span className="sm:hidden">Код</span><span className="hidden sm:inline">Купон код</span></>,
                          value: 'coupon',
                          content: (
                            <div className="pt-2">
                              {appliedCoupon ? (() => {
                                const d = appliedCoupon.discount || appliedCoupon
                                const code = appliedCoupon.code || d?.code
                                const name = d?.name
                                const benefitParts: string[] = []
                                if (d?.valueType === 'percent' && d?.valuePercent) benefitParts.push(`−${d.valuePercent}%`)
                                else if (d?.valueType === 'amount' && d?.valueAmount) benefitParts.push(`−${formatPrice(d.valueAmount)}`)
                                else if (d?.valueType === 'free') benefitParts.push('Үнэгүй')
                                if (d?.type === 'freeShipping' || d?.freeShipping) benefitParts.push('Үнэгүй хүргэлт')
                                if (couponDiscount > 0) benefitParts.push(`−${formatPrice(couponDiscount)}`)
                                return (
                                  <div className="flex items-start justify-between gap-3 rounded-2xl bg-emerald-50 border border-emerald-100 p-3.5">
                                    <span className="w-9 h-9 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center shrink-0">
                                      <Tag className="size-4" strokeWidth={2.25} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[13.5px] font-semibold text-emerald-800 truncate">{name || code || 'Купон идэвхтэй'}</span>
                                        {benefitParts[0] && (
                                          <span className="inline-flex items-center px-1.5 h-[18px] rounded-md bg-emerald-100 text-emerald-700 text-[11px] font-bold tabular-nums">{benefitParts[0]}</span>
                                        )}
                                      </div>
                                      {code && code !== name && (
                                        <div className="mt-0.5 text-[11.5px] font-mono text-emerald-700/80 tracking-wide truncate">{code}</div>
                                      )}
                                      {d?.minOrderAmount > 0 && (
                                        <div className="mt-0.5 text-[11.5px] text-emerald-700/65">
                                          {formatPrice(d.minOrderAmount)}-с дээш захиалгад
                                        </div>
                                      )}
                                    </div>
                                    <button onClick={() => removeCoupon()} aria-label="Хасах" className="w-7 h-7 inline-flex items-center justify-center rounded-full text-emerald-700/70 hover:text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer shrink-0">
                                      <X className="size-3.5" />
                                    </button>
                                  </div>
                                )
                              })() : (
                                <>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 flex items-center gap-2.5 h-11 px-4 rounded-full bg-[#F1EEE7]">
                                      <Tag className="size-3.5 text-foreground/55" />
                                      <input
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        placeholder="Купон код"
                                        className="flex-1 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus:border-0 text-[13px] text-foreground placeholder:text-foreground/50"
                                      />
                                    </div>
                                    <button onClick={onApplyCoupon} disabled={couponLoading || !couponCode.trim()} className="h-11 px-4 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                                      {couponLoading ? '...' : 'Хэрэглэх'}
                                    </button>
                                  </div>
                                  {couponError && <div className="mt-1.5 text-[12px] text-red-600">{couponError}</div>}
                                </>
                              )}
                            </div>
                          ),
                        }] : []),
                        ...(canUseGiftCard ? [{
                          label: <><span className="sm:hidden">Карт</span><span className="hidden sm:inline">Бэлгийн карт</span></>,
                          value: 'giftcard',
                          content: (
                            <div className="pt-2">
                              {appliedGiftCard ? (
                                <div className="flex items-center justify-between gap-2 h-11 px-4 rounded-full bg-emerald-50 border border-emerald-100">
                                  <span className="text-[13px] font-semibold text-emerald-700 truncate inline-flex items-center gap-1.5">
                                    <Gift className="size-3.5" /> −{formatPrice(giftCardDiscount)}
                                  </span>
                                  <button onClick={() => removeGiftCard()} aria-label="Хасах" className="w-7 h-7 inline-flex items-center justify-center rounded-full text-emerald-700/70 hover:text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer">
                                    <X className="size-3.5" />
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 flex items-center gap-2.5 h-11 px-4 rounded-full bg-[#F1EEE7]">
                                      <Gift className="size-3.5 text-foreground/55" />
                                      <input
                                        value={giftCode}
                                        onChange={(e) => setGiftCode(e.target.value)}
                                        placeholder="Бэлгийн карт"
                                        className="flex-1 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus:border-0 text-[13px] text-foreground placeholder:text-foreground/50"
                                      />
                                    </div>
                                    <button onClick={onApplyGift} disabled={giftLoading || !giftCode.trim()} className="h-11 px-4 rounded-full bg-foreground text-cream text-[13px] font-semibold hover:bg-foreground/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
                                      {giftLoading ? '...' : 'Хэрэглэх'}
                                    </button>
                                  </div>
                                  {giftError && <div className="mt-1.5 text-[12px] text-red-600">{giftError}</div>}
                                </>
                              )}
                            </div>
                          ),
                        }] : []),
                        ...(canUseLoyaltyVoucher ? [{
                          label: 'Ваучер',
                          value: 'loyalty',
                          content: (
                            <div className="pt-2">
                              {appliedLoyaltyVoucher ? (
                                <>
                                  <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                                    <span className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center shrink-0">
                                      <Ticket className="size-4" strokeWidth={2.25} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[13px] font-semibold font-mono text-foreground truncate">{appliedLoyaltyVoucher.code}</div>
                                      <div className="text-[11.5px] text-emerald-700 mt-0.5 inline-flex items-center gap-1"><CircleCheck className="size-3" /> Идэвхжсэн</div>
                                    </div>
                                    <div className="text-[13px] font-bold tabular-nums text-emerald-700 shrink-0">−{formatPrice(appliedVoucherAmount)}</div>
                                    <button onClick={() => { removeLoyaltyVoucher(); toast.success('Ваучер хасагдлаа') }} aria-label="Хасах" className="w-7 h-7 inline-flex items-center justify-center rounded-full text-emerald-700/70 hover:text-emerald-700 hover:bg-emerald-100 transition-colors cursor-pointer shrink-0">
                                      <X className="size-3.5" />
                                    </button>
                                  </div>
                                  {!loyaltyProgram?.voucherPartialUse && voucherExceedsCart ? (
                                    <div className="mt-2 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                                      <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                                      <div className="text-[12.5px] leading-snug">
                                        <div className="font-semibold text-amber-800">Анхаар!</div>
                                        <div className="text-amber-700 mt-0.5">Ваучер захиалгад бүтнээрээ зарцуулагдана. Захиалгын дүн ваучерын дүнгээс бага тул үлдэх <span className="font-semibold tabular-nums">{formatPrice(appliedVoucherAmount - subPrice)}</span> буцаагдахгүй.</div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-2 flex items-start gap-1.5 px-1 text-[12px] text-foreground/55">
                                      <Info className="size-3.5 mt-0.5 shrink-0" />
                                      <span>{loyaltyProgram?.voucherPartialUse ? 'Ашиглаагүй үлдэгдэл хадгалагдаж, дараагийн захиалгад ашиглагдана.' : 'Ваучер захиалгад бүтнээрээ зарцуулагдана.'}</span>
                                    </div>
                                  )}
                                </>
                              ) : loyaltyVouchers.length === 0 ? (
                                <div className="text-[12.5px] text-foreground/55 px-1">
                                  Танд идэвхтэй ваучер алга байна. <a href={navigateUrl('/loyalty', pathname)} className="underline hover:text-foreground">Оноогоо ваучер болгох</a>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {loyaltyVouchers.map((voucher: any) => (
                                    <div key={voucher.id} className="flex items-center gap-3 rounded-2xl border border-foreground/10 bg-white p-3">
                                      <span className="w-9 h-9 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                                        <Ticket className="size-4" strokeWidth={2.25} />
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-semibold font-mono text-foreground truncate">{voucher.code}</div>
                                        <div className="text-[11.5px] text-foreground/55 mt-0.5">{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
                                      </div>
                                      <div className="text-[13px] font-bold tabular-nums text-foreground shrink-0">{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
                                      <button onClick={() => { applyLoyaltyVoucher(voucher); toast.success('Ваучер идэвхжлээ') }} className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold hover:bg-foreground/90 transition-colors cursor-pointer shrink-0">Ашиглах</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ),
                        }] : []),
                      ]}
                    />
                  )}

                  {belowMinAmount && (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                      <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] leading-snug">
                        <div className="font-semibold text-amber-800">Захиалгын доод дүнд хүрээгүй байна</div>
                        <div className="text-amber-700 mt-0.5">Хамгийн багадаа <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount)}</span>-ийн захиалга хийх ёстой. <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subPrice)}</span> дутуу байна.</div>
                        <div className="mt-2 h-1.5 rounded-full bg-amber-200 overflow-hidden">
                          <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, (subPrice / vendor.orderMinAmount) * 100)}%` }} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 text-[14px]">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground/60">Барааны үнэ</span>
                      <span className="text-foreground font-medium">{formatPrice(amount)}</span>
                    </div>
                    {displaySaleSavings > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Хямдралын дүн</span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(displaySaleSavings)}</span>
                      </div>
                    )}
                    {discountBreakdown?.map((d: any) => (
                      <div key={d.id} className="flex items-center justify-between">
                        <span className="text-foreground/60 flex flex-col">
                          <span>Урамшуулал</span>
                          <span className="text-[11.5px] text-foreground/45">({d.name})</span>
                        </span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(d.amount)}</span>
                      </div>
                    ))}
                    {couponDiscount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Купон урамшуулал</span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(couponDiscount)}</span>
                      </div>
                    )}
                    {orderDiscountAmount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Захиалгын хөнгөлөлт</span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(orderDiscountAmount)}</span>
                      </div>
                    )}
                    {appliedGiftCard && giftCardDiscount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60 inline-flex items-center gap-1.5">
                          <Gift className="size-3.5" /> Бэлгийн карт{appliedGiftCard?.code ? ` · ${appliedGiftCard.code}` : ''}
                        </span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(giftCardDiscount)}</span>
                      </div>
                    )}
                    {loyaltyDiscount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Лоялти хөнгөлөлт</span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(loyaltyDiscount + loyaltySaleOffsetAmount)}</span>
                      </div>
                    )}
                    {validLoyaltyVoucherDiscount > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60 inline-flex items-center gap-1.5">
                          <Ticket className="size-3.5" /> Лоялти ваучер{appliedLoyaltyVoucher?.code ? ` · ${appliedLoyaltyVoucher.code}` : ''}
                        </span>
                        <span className="text-emerald-600 font-medium">−{formatPrice(validLoyaltyVoucherDiscount)}</span>
                      </div>
                    )}
                    {selectedDelivery === 'delivery' && showDeliverySection && (
                      <div className="flex items-center justify-between">
                        <span className="text-foreground/60">Хүргэлт</span>
                        <span className={`font-medium ${vendor?.deliveryType === 'laterDelivery' ? 'text-amber-600' : (deliveryPrice === 0 ? 'text-emerald-600' : 'text-foreground')}`}>
                          {vendor?.deliveryType === 'laterDelivery' ? 'Нэмэлтээр тооцно' : (deliveryPrice === 0 ? 'Үнэгүй' : formatPrice(deliveryPrice))}
                        </span>
                      </div>
                    )}
                    {freeShippingProgress && showDeliverySection && (
                      <div className="rounded-2xl bg-[#F1EEE7] p-3 mt-1">
                        <div className="flex items-center justify-between mb-1.5 text-[12.5px]">
                          <span className="inline-flex items-center gap-1.5 text-foreground font-semibold"><Truck className="size-3.5" /> Үнэгүй хүргэлт</span>
                          {freeShippingProgress.reached
                            ? <span className="text-emerald-600 font-semibold">Хангагдсан</span>
                            : <span className="text-foreground/55">{formatPrice(freeShippingProgress.remaining)} дутуу</span>}
                        </div>
                        <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                          <div className="h-full bg-foreground transition-all" style={{ width: `${Math.min(100, freeShippingProgress.percentage || 0)}%` }} />
                        </div>
                      </div>
                    )}
                    {hasFreeShipping && (
                      <div className="text-[12px] text-emerald-600 font-semibold inline-flex items-center gap-1.5">
                        <Truck className="size-3.5" /> {activeOrderDiscount?.type === 'freeShipping' ? (activeOrderDiscount.name || 'Үнэгүй хүргэлт') : appliedCoupon?.discount?.name || appliedCoupon?.name || 'Үнэгүй хүргэлт'}
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-foreground/10 flex items-baseline justify-between">
                    <span className="text-[15px] font-semibold text-foreground">Нийт төлөх</span>
                    <span className="text-[32px] font-bold font-gilroy tracking-[-0.02em] text-foreground">{formatPrice(totalPrice)}</span>
                  </div>

                  {vendor?.isDanVerified === false && (
                    <div className="flex gap-2.5 p-3 bg-amber-50 border border-amber-100 rounded-2xl">
                      <Info className="size-3.5 text-amber-700 shrink-0 mt-0.5" />
                      <div className="text-[11.5px] font-medium leading-snug text-amber-900">
                        <span className="font-bold">{vendor?.name}</span> дэлгүүр нь <span className="font-bold">Улсын бүртгэлээр баталгаажаагүй</span> тул дэлгүүрийн үйлчилгээний нөхцөлтэй сайтар танилцсаны дараа эрсдэлийг тооцож худалдан авалт хийхийг зөвлөж байна.
                      </div>
                    </div>
                  )}

                  {cart?.products?.some((p: any) => p.quantity === 0 || p.isOutOfStock) && (
                    <div className="flex gap-2.5 p-3 bg-red-50 border border-red-100 rounded-2xl">
                      <PackageX className="size-4 text-red-500 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] leading-snug">
                        <div className="font-semibold text-red-600">Үлдэгдэл дууссан бараа</div>
                        <div className="text-red-600/85 mt-0.5">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү.</div>
                      </div>
                    </div>
                  )}

                  <label className="flex items-start gap-2.5 text-[12.5px] text-foreground/70 cursor-pointer">
                    <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-foreground/20 accent-foreground" />
                    <span>
                      Би <span className="font-semibold text-foreground">{vendor?.name}</span> дэлгүүрийн{' '}
                      <button type="button" onClick={() => openTerms('service')} className="font-semibold text-foreground underline underline-offset-2 cursor-pointer">Үйлчилгээ</button>,{' '}
                      <button type="button" onClick={() => openTerms('delivery')} className="font-semibold text-foreground underline underline-offset-2 cursor-pointer">Хүргэлт</button>,{' '}
                      <button type="button" onClick={() => openTerms('payment')} className="font-semibold text-foreground underline underline-offset-2 cursor-pointer">Төлбөр</button>{' '}нөхцөлтэй танилцсан.
                    </span>
                  </label>

                  <Button
                    width="full"
                    isLoading={loading}
                    onClick={submitCheckout}
                    icon={Wallet}
                    iconPosition="left"
                    variant="outline"
                    className="w-full h-14 rounded-full! bg-foreground text-cream font-semibold! border-foreground hover:bg-foreground/90 hover:text-cream transition-all"
                    disabled={isValidating || !canCheckout() || !accepted}
                  >
                    {effectiveDeposit > 0 ? 'Урьдчилгаа төлөх' : 'Төлөх'} · {formatPrice(effectiveDeposit > 0 ? effectiveDeposit : totalPrice)}
                  </Button>

                  <a href={navigateUrl('/product', pathname)} className="inline-flex items-center justify-center gap-1.5 text-[13px] text-foreground/55 hover:text-foreground transition-colors">
                    Дахин бүтээгдэхүүн нэмэх <ChevronRight className="size-3.5" />
                  </a>
                </div>
              </aside>
            </div>
          )}
        </div>
      </section>

      <Modal
        open={openAddNewAddress}
        setOpen={setOpenAddNewAddress}
        saveAction={addNewAddress}
        isSaveActive={!newAddressLoading}
        showCancel={false}
        isLoading={newAddressLoading}
        saveText={newAddressLoading ? 'Хаягийг хадгалж байна...' : 'Хадгалах'}
        title="Шинэ хаяг оруулах"
        description=""
      >
        <div className="flex flex-col gap-4">
          <Tabs
            tabs={[
              { label: 'Орон сууц', value: 'apartment', content: null },
              { label: 'Хашаа байшин', value: 'house', content: null },
              { label: 'Оффис', value: 'office', content: null },
            ]}
            defaultValue={addressType}
            onValueChange={(v: string) => setAddressType(v as 'apartment' | 'house' | 'office')}
            className="w-full"
          />
          <Select label="Хот / Аймаг" items={provinces} value={selectedProvince || ''} onChange={(v: string) => { setSelectedProvince(v); setCity(v); setSelectedDistrict(null); setDuureg(''); setSelectedSubdistrict(null); setKhoroo('') }} required />
          <Select label="Дүүрэг / Сум" value={selectedDistrict || ''} items={districts} required onChange={(v: string) => { setSelectedDistrict(v); setDuureg(v); setSelectedSubdistrict(null); setKhoroo('') }} />
          <Select label="Хороо / Баг" value={selectedSubdistrict || ''} items={subdistricts} required onChange={(v: string) => { setSelectedSubdistrict(v); setKhoroo(v) }} />
          {addressType === 'apartment' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Байр" placeholder="Байрны дугаар" value={bair} onChange={(e: any) => setBair(e.target.value)} required />
              <Input label="Тоот" placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} required />
            </div>
          )}
          {addressType === 'house' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Хашааны дугаар" placeholder="Хашааны дугаар" value={houseNum} onChange={(e: any) => setHouseNum(e.target.value)} required />
              <Input label="Гудамж" placeholder="Гудамжны нэр" value={street} onChange={(e: any) => setStreet(e.target.value)} />
            </div>
          )}
          {addressType === 'office' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={building} onChange={(e: any) => setBuilding(e.target.value)} required />
                <Input label="Давхар" placeholder="Давхар" value={floor} onChange={(e: any) => setFloor(e.target.value)} required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Тоот" placeholder="Тоот" value={toot} onChange={(e: any) => setToot(e.target.value)} required />
                <Input label="Оффисын нэр" placeholder="Оффисын нэр" value={officeName} onChange={(e: any) => setOfficeName(e.target.value)} />
              </div>
            </>
          )}
          <Input label="Нэмэлт мэдээлэл" placeholder="Орц, давхар, орцны код гэх мэт" value={additionalAddress} onChange={(e: any) => setAdditionalAddress(e.target.value)} />
        </div>
      </Modal>
    </>
  )
}

function CustomField({ field, value, onChange, onFile, uploading, idPrefix }: { field: any, value: any, onChange: (v: any) => void, onFile: (f: File) => void, uploading: boolean, idPrefix: string }) {
  if (field.type === 'text' || field.type === 'number') {
    return <Input label={field.name} type={field.type} placeholder={`${field.name} оруулна уу`} value={value || ''} onChange={(e: any) => onChange(e.target.value)} required={field.required} />
  }
  if (field.type === 'textarea') {
    return <Textarea label={field.name} placeholder={`${field.name} оруулна уу`} value={value || ''} onChange={(e: any) => onChange(e.target.value)} required={field.required} />
  }
  if (field.type === 'select') {
    const items = (field.options || []).map((opt: string) => ({ label: opt, value: opt }))
    return <Select label={field.name} items={items} value={value || ''} onChange={(v: string) => onChange(v)} required={field.required} />
  }
  if (field.type === 'checkbox') {
    return <Checkbox title={field.name} description="" checked={value === 'true' || value === true} onChange={() => onChange(value === 'true' || value === true ? 'false' : 'true')} required={field.required} />
  }
  if (field.type === 'date') {
    const dateValue = value ? (typeof value === 'string' ? new Date(value) : value) : null
    return <DateTimePicker label={field.name} date={dateValue} setDate={(d: Date | undefined) => onChange(d ? d.toISOString() : '')} placeholder={`${field.name} сонгоно уу`} required={field.required} className="w-full" />
  }
  if (field.type === 'productImage') {
    return <ImagePicker label={field.name} images={field.options || []} value={value} multiple={field.multiple} onChange={(v) => onChange(v)} required={field.required} />
  }
  if (field.type === 'file') {
    const fileUrl = typeof value === 'string' ? value : value?.url
    const isImage = fileUrl && fileUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    const isPdf = fileUrl && fileUrl.match(/\.pdf$/i)
    const inputId = `${idPrefix}-${field.name}`
    return (
      <div>
        <div className="text-[13px] font-semibold text-foreground mb-2">
          {field.name}{field.required && <span className="text-red-600 ml-1">*</span>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => !uploading && document.getElementById(inputId)?.click()}
            className="relative w-16 h-16 rounded-xl border border-foreground/15 bg-white inline-flex items-center justify-center overflow-hidden hover:border-foreground/40 transition-colors cursor-pointer"
          >
            {uploading ? <LoaderCircle className="size-5 animate-spin text-foreground/55" />
              : isImage ? <img src={formatImageURL(fileUrl)} alt="" className="w-full h-full object-cover" />
                : isPdf ? <div className="text-[10px] font-bold text-red-600">PDF</div>
                  : <FileUp className="size-5 text-foreground/45" />}
          </button>
          <div className="flex-1 min-w-0">
            <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => !uploading && document.getElementById(inputId)?.click()}>
              {uploading ? 'Хуулж байна...' : fileUrl ? 'Солих' : 'Файл сонгох'}
            </Button>
            {value && (
              <div className="mt-1 text-[12px] text-foreground/55 truncate">
                {typeof value === 'string' ? value.split('/').pop() : value?.originalName}
              </div>
            )}
          </div>
          <input id={inputId} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f) }} />
        </div>
      </div>
    )
  }
  return null
}

function SectionCard({ n, title, children }: { n: number, title: string, children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-3xl p-6 lg:p-7">
      <header className="flex items-center gap-3 mb-5">
        <span className="w-7 h-7 rounded-full bg-foreground text-cream inline-flex items-center justify-center text-[12px] font-bold shrink-0">{n}</span>
        <h3 className="text-[18px] sm:text-[20px] font-bold font-gilroy tracking-[-0.015em] text-foreground">{title}</h3>
      </header>
      {children}
    </section>
  )
}

function CheckoutSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-7 lg:gap-10">
      <div className="space-y-4">
        <Skeleton className="h-12 w-3/4 rounded-2xl" />
        {[0, 1, 2].map(i => (
          <div key={i} className="bg-white rounded-3xl p-6 space-y-3">
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-11 w-full rounded-lg" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
        ))}
      </div>
      <div className="bg-white rounded-3xl p-6 space-y-4">
        <Skeleton className="h-7 w-32" />
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="w-18 h-18 rounded-2xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-16" />
          </div>
        ))}
        <Skeleton className="h-14 w-full rounded-full" />
      </div>
    </div>
  )
}
